import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { filterPhone } from '../utils/inputValidation';
import { getPlaceholders } from '../utils/placeholders';
import { useToast } from './Toast';
import {
  Building2, MapPin, Phone, Link, Clock, Save, Loader2, Receipt, CalendarClock,
} from 'lucide-react';

const ALL_FREQUENCIES = [
  { value: 7,  label: '1 semaine' },
  { value: 14, label: '2 semaines' },
  { value: 21, label: '3 semaines' },
  { value: 28, label: '4 semaines' },
  { value: 35, label: '5 semaines' },
  { value: 30, label: '1 mois' },
  { value: 60, label: '2 mois' },
  { value: 90, label: '3 mois' },
];

const JOURS = [
  { jour: 1, label: 'Lundi' },
  { jour: 2, label: 'Mardi' },
  { jour: 3, label: 'Mercredi' },
  { jour: 4, label: 'Jeudi' },
  { jour: 5, label: 'Vendredi' },
  { jour: 6, label: 'Samedi' },
  { jour: 0, label: 'Dimanche' },
];

const DEFAULT_HORAIRES = JOURS.map(({ jour }) => ({
  jour,
  ouvert: jour >= 1 && jour <= 5,
  debut: '09:00',
  fin: '18:00',
}));

// ─── Petit input réutilisable ──────────────────────────────────────────────────
function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <label className="flex items-center gap-2 text-xs font-black text-slate-500 uppercase tracking-wider mb-2">
        <Icon size={13} />
        {label}
      </label>
      {children}
    </div>
  );
}

const inputCls =
  'w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-bold text-slate-800 outline-none focus:ring-2 ring-blue-500/20 transition-all placeholder:font-normal placeholder:text-slate-400';

// ─── Composant principal ───────────────────────────────────────────────────────
const AdminSalonSettings = ({ salon: initialSalon, onSaved }) => {
  const showToast = useToast();
  const [saving, setSaving]   = useState(false);

  const [nom,            setNom]            = useState('');
  const [adresse,        setAdresse]        = useState('');
  const [telephone,      setTelephone]      = useState('');
  const [lienGoogleMaps, setLienGoogleMaps] = useState('');
  const [horaires,       setHoraires]       = useState(DEFAULT_HORAIRES);
  const [tvaEnabled,          setTvaEnabled]          = useState(false);
  const [tauxTva,             setTauxTva]             = useState(18);
  const [allowedFrequencies,  setAllowedFrequencies]  = useState([7, 14, 21, 28, 35, 30, 60, 90]);
  const devise = initialSalon?.devise || 'XOF';

  // Initialise le formulaire depuis les données du salon parent
  useEffect(() => {
    if (!initialSalon) return;
    setNom(initialSalon.nom            || '');
    setAdresse(initialSalon.adresse    || '');
    setTelephone(initialSalon.telephone  || '');
    setLienGoogleMaps(initialSalon.lienGoogleMaps || '');

    setTvaEnabled(initialSalon.tvaEnabled ?? false);
    setTauxTva(initialSalon.tauxTva ?? 18);
    setAllowedFrequencies(initialSalon.allowedFrequencies?.length ? initialSalon.allowedFrequencies : [7, 14, 21, 28, 35, 30, 60, 90]);

    if (initialSalon.horaires?.length) {
      // Fusionne les horaires reçus avec les jours par défaut (au cas où un jour manque)
      const merged = JOURS.map(({ jour }) => {
        const existing = initialSalon.horaires.find(h => h.jour === jour);
        return existing
          ? { jour, ouvert: existing.ouvert, debut: existing.debut, fin: existing.fin }
          : { jour, ouvert: jour >= 1 && jour <= 5, debut: '09:00', fin: '18:00' };
      });
      setHoraires(merged);
    }
  }, [initialSalon]);

  const setHoraire = (jour, field, value) =>
    setHoraires(prev =>
      prev.map(h => h.jour === jour ? { ...h, [field]: value } : h)
    );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await api.put('/salon/me', {
        nom, adresse, telephone, lienGoogleMaps, horaires,
        allowedFrequencies,
        ...(devise !== 'EUR' && { tvaEnabled, tauxTva: Number(tauxTva) }),
      }, { headers: { Authorization: `Bearer ${token}` } });

      showToast('Informations mises à jour !', 'success');
      onSaved?.(data.data);
    } catch {
      showToast('Erreur lors de la mise à jour.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 pb-8 animate-in fade-in slide-in-from-bottom-4 duration-500">

      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-black text-slate-900 flex items-center gap-3">
          <Building2 className="text-blue-600" size={28} /> Mon salon
        </h1>
        <p className="text-slate-500 font-medium text-sm mt-1">
          Informations visibles par vos clients
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">

        {/* ── Infos générales ─────────────────────────────────────────────── */}
        <div className="bg-white border border-slate-100 rounded-[2rem] p-6 shadow-sm space-y-5">
          <p className="text-xs font-black text-slate-400 uppercase tracking-widest">
            Informations générales
          </p>

          <Field label="Nom du salon" icon={Building2}>
            <input
              className={inputCls}
              value={nom}
              onChange={e => setNom(e.target.value)}
              placeholder="Ex : Salon Beauty"
              required
            />
          </Field>

          <Field label="Adresse" icon={MapPin}>
            <input
              className={inputCls}
              value={adresse}
              onChange={e => setAdresse(e.target.value)}
              placeholder="Ex : 12 rue des Fleurs, Dakar"
            />
          </Field>

          <Field label="Téléphone" icon={Phone}>
            <input
              className={inputCls}
              value={telephone}
              onChange={e => setTelephone(filterPhone(e.target.value))}
              placeholder={getPlaceholders(devise).telephone}
            />
          </Field>

          <Field label="Lien Google Maps" icon={Link}>
            <input
              className={inputCls}
              value={lienGoogleMaps}
              onChange={e => setLienGoogleMaps(e.target.value)}
              placeholder="https://maps.google.com/..."
              type="url"
            />
            {lienGoogleMaps && (
              <a
                href={lienGoogleMaps}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 mt-2 text-xs font-bold text-blue-600 hover:underline"
              >
                <MapPin size={11} /> Aperçu du lien
              </a>
            )}
            <p className="text-[11px] text-slate-400 font-medium mt-1.5">
              Ce lien sera envoyé dans l'email de confirmation du client.
            </p>
          </Field>
        </div>

        {/* ── Horaires d'ouverture ─────────────────────────────────────────── */}
        <div className="bg-white border border-slate-100 rounded-[2rem] p-6 shadow-sm space-y-4">
          <p className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
            <Clock size={13} /> Horaires d'ouverture
          </p>

          <div className="space-y-3">
            {JOURS.map(({ jour, label }) => {
              const h = horaires.find(x => x.jour === jour) || {};
              return (
                <div key={jour} className="flex items-center gap-3 flex-wrap">

                  {/* Toggle ouvert / fermé */}
                  <button
                    type="button"
                    onClick={() => setHoraire(jour, 'ouvert', !h.ouvert)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${
                      h.ouvert ? 'bg-blue-600' : 'bg-slate-200'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                      h.ouvert ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>

                  {/* Nom du jour */}
                  <span className={`w-24 text-sm font-black ${h.ouvert ? 'text-slate-800' : 'text-slate-300'}`}>
                    {label}
                  </span>

                  {/* Heures */}
                  {h.ouvert ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="time"
                        value={h.debut}
                        onChange={e => setHoraire(jour, 'debut', e.target.value)}
                        className="px-3 py-1.5 bg-slate-50 border border-slate-100 rounded-xl text-sm font-bold outline-none focus:ring-2 ring-blue-500/20"
                      />
                      <span className="text-slate-300 font-bold text-sm">-</span>
                      <input
                        type="time"
                        value={h.fin}
                        onChange={e => setHoraire(jour, 'fin', e.target.value)}
                        className="px-3 py-1.5 bg-slate-50 border border-slate-100 rounded-xl text-sm font-bold outline-none focus:ring-2 ring-blue-500/20"
                      />
                    </div>
                  ) : (
                    <span className="text-xs font-bold text-slate-300 uppercase tracking-wider">Fermé</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── TVA (salons non-EUR uniquement) ─────────────────────────────── */}
        {devise !== 'EUR' && (
          <div className="bg-white border border-slate-100 rounded-[2rem] p-6 shadow-sm space-y-4">
            <p className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
              <Receipt size={13} /> Facturation &amp; TVA
            </p>

            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-black text-slate-800">Appliquer la TVA sur les factures</p>
                <p className="text-xs text-slate-400 font-medium mt-0.5">
                  Affiche le détail HT / TVA sur chaque facture générée
                </p>
              </div>
              <button
                type="button"
                onClick={() => setTvaEnabled(v => !v)}
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                  tvaEnabled ? 'bg-blue-600' : 'bg-slate-200'
                }`}
              >
                <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                  tvaEnabled ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>

            {tvaEnabled && (
              <div className="flex items-center gap-3">
                <label className="text-xs font-black text-slate-500 uppercase tracking-wider shrink-0">
                  Taux TVA
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={tauxTva}
                    onChange={e => setTauxTva(e.target.value)}
                    className="w-24 px-4 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-bold text-slate-800 outline-none focus:ring-2 ring-blue-500/20"
                  />
                  <span className="text-sm font-black text-slate-500">%</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Fréquences de suivi client ──────────────────────────────────── */}
        <div className="bg-white border border-slate-100 rounded-[2rem] p-6 shadow-sm space-y-4">
          <p className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
            <CalendarClock size={13} /> Fréquences de suivi client
          </p>
          <p className="text-xs text-slate-400 font-medium">
            Sélectionnez les intervalles disponibles dans le menu de rappel client.
          </p>
          <div className="flex flex-wrap gap-2">
            {ALL_FREQUENCIES.map(({ value, label }) => {
              const active = allowedFrequencies.includes(value);
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => setAllowedFrequencies(prev =>
                    active ? prev.filter(v => v !== value) : [...prev, value]
                  )}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all border ${
                    active
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-slate-50 text-slate-500 border-slate-200 hover:border-blue-300'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
          {allowedFrequencies.length === 0 && (
            <p className="text-xs text-red-500 font-medium">Sélectionnez au moins une fréquence.</p>
          )}
        </div>

        {/* ── Bouton sauvegarder ───────────────────────────────────────────── */}
        <button
          type="submit"
          disabled={saving}
          className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3.5 bg-blue-600 text-white font-black rounded-2xl hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-500/20"
        >
          {saving
            ? <><Loader2 size={18} className="animate-spin" /> Enregistrement...</>
            : <><Save size={18} /> Enregistrer</>
          }
        </button>
      </form>
    </div>
  );
};

export default AdminSalonSettings;
